import { type FC, useEffect, useMemo, useState, useCallback, type ReactNode } from 'react';
import { AutoComplete } from 'antd';
import { CodeOutlined } from '@ant-design/icons';
import styles from './ai-assistant.module.scss';

export type ShortCodeOption = {
  key: string;
  prefix: string; // e.g. '/scripts'
  category: string; // e.g. 'scripts'
  label: string; // Display name
  description: string; // Helpful description for context menu
  example: string; // Usage example
};

export const DEFAULT_SHORT_CODES: ShortCodeOption[] = [
  {
    key: 'scripts',
    prefix: '/scripts',
    category: 'scripts',
    label: '/scripts',
    description: 'Search call center & support scripts',
    example: '/scripts cancellation',
  },
  {
    key: 'pricing',
    prefix: '/pricing',
    category: 'pricing',
    label: '/pricing',
    description: 'Search pricing schedules and services',
    example: '/pricing tv',
  },
  {
    key: 'policies',
    prefix: '/policies',
    category: 'policies',
    label: '/policies',
    description: 'Search company & store policies',
    example: '/policies refund',
  },
  {
    key: 'procedures',
    prefix: '/procedures',
    category: 'procedures',
    label: '/procedures',
    description: 'Search standard operating procedures',
    example: '/procedures escalation',
  },
];

export type ShortCodeProps = {
  inputValue: string;
  onSelectShortCode: (prefix: string) => void;
  shortCodes?: ShortCodeOption[];
  children: ReactNode;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
};

export const ShortCodes: FC<ShortCodeProps> = ({
  inputValue,
  onSelectShortCode,
  shortCodes = DEFAULT_SHORT_CODES,
  children,
  onKeyDown,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  /**
   * Filter short code options based on current input typed after '/'
   */
  const activeSlashMatch = useMemo(() => {
    const match = inputValue.match(/^\/([a-zA-Z0-9_-]*)$/);
    if (match) {
      const typedPrefix = match[1].toLowerCase();
      return shortCodes.filter(
        (code) =>
          code.category.toLowerCase().startsWith(typedPrefix) ||
          code.prefix.toLowerCase().startsWith(`/${typedPrefix}`),
      );
    }
    return [];
  }, [inputValue, shortCodes]);

  useEffect(() => {
    if (activeSlashMatch.length > 0) {
      setIsMenuOpen(true);
    } else {
      setIsMenuOpen(false);
    }
  }, [activeSlashMatch]);

  const handleSelect = useCallback(
    (value: string) => {
      onSelectShortCode(value);
      setIsMenuOpen(false);
    },
    [onSelectShortCode],
  );

  /**
   * Format options for Ant Design's AutoComplete component
   */
  const autoCompleteOptions = useMemo(
    () =>
      DEFAULT_SHORT_CODES.map((code) => ({
        value: `${code.prefix} `,
        label: (
          <div className={styles.aiAssistant__shortCodeItem}>
            <div className={styles.aiAssistant__shortCodeTitle}>
              <CodeOutlined className={styles.aiAssistant__shortCodeIcon} />
              <strong>{code.prefix}</strong>
              <span className={styles.aiAssistant__shortCodeExample}>e.g. {code.example}</span>
            </div>
            <div className={styles.aiAssistant__shortCodeDesc}>{code.description}</div>
          </div>
        ),
      })),
    [activeSlashMatch],
  );

  return (
    <div className={styles.aiAssistant__inputWrapper} onKeyDown={onKeyDown}>
      <AutoComplete
        classNames={{
          popup: {
            root: 'styles.aiAssistant__autoCompletePopup',
          },
        }}
        open={isMenuOpen}
        options={autoCompleteOptions}
        onSelect={handleSelect}
        getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
        style={{ width: '100%' }}
      >
        <div>{children}</div>
      </AutoComplete>
    </div>
  );
};
